import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  CardActions,
  Button,
  useTheme,
  Paper,
} from '@mui/material';
import DATLogo from 'assets/icons/dat-tm-logo.jpg';
import MotiveLogo from 'assets/icons/motive-logo.png';
import SamsaraLogo from 'assets/icons/samsara-logo.png';
import { connect } from 'react-redux';
import { activateVendorIntegration, loginVendorPortal } from 'services/integration-service';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import InputField from 'components/Input-Field/input-field';
import DataDialog from 'components/dialogs/DataDialog';
import { FSConfig } from 'freightsmart-config';
import { tokens } from 'theme/tokens';

const ThirdPartyIntegration = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [organization, setOrganization] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [isVendorLoginDialogOpen, setVendorLoginDialogOpen] = useState(false);

  const initState = {
    vendorLoginObj: {
      serviceAccountUsername: '', //Email
      serviceAccountPassword: '', //orgPassword 
      individualUsername: '',
    }
  }

  const INTEGRATIONS = [
    {
      vendor: 'DAT',
      description: 'DAT Freight & Analytics operates DAT One, North America’s largest truckload freight marketplace.',
      logo: DATLogo,
    },
    {
      vendor: 'Motive',
      description: 'Manage all aspects of fleet operations on a single platform that’s easy to deploy, customize & use.',
      logo: MotiveLogo,
    },
    {
      vendor: 'Samsara',
      description: 'Improve the safety, efficiency, and sustainability of the operations that power the global economy.',
      logo: SamsaraLogo,
    },
  ];

  const getChipColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return colors.success.dark;
      case 'INACTIVE':
        return colors.info.main;
    }
  };

  const activateIntegration = (vendor) => {
    const { tenant, users } = props;
    const { tenantId, company, email, phone, street, city, state, zipcode, country } = tenant;
    const activeUser = users && users.filter(u => u.email === localStorage.getItem('ACTIVE_USER_EMAIL'))[0];
    const org = {
      name: company,
      companyDetails: {
        email: email,
        primaryPhone: phone,
        address: [
          {
            name: company,
            street,
            city,
            state,
            zipcode,
            country,
            isPrimary: true,
          }
        ]
      }
    };
    const activateTenant = {
      tenantType: 'CARRIER',
      tenantId: tenantId,
      company: org, /* Same for now */
    };
    const activateUser = {
      firstName: activeUser ? activeUser.firstName : '',
      lastName: activeUser ? activeUser.lastName : '',
      email: activeUser ? activeUser.email : '',
    };

    const payload = {
      company: org,
      tenant: activateTenant,
      user: activateUser
    };
    activateVendorIntegration(payload).then(res => {
      if (res.status === 200 && Object.keys(res.data).length !== 0) {
        const { name, organizationId, registrationDate, company, rootUser, tenants } = res.data;
        const org = {
          company,
          name,
          organizationId,
          rootUser,
          tenants
        };
        setOrganization(org);
        localStorage.setItem(vendor, 'Activated');
      } else {
        alert('Unable to activate vendor. Please try again or contact our support team at support@freightsmart.ai');
      }
    });
  };

  const handleLoginForm = (vendor) => {
    setLoading(true);
    setToast({ type: 'loading', message: 'Please wait' });
    const payload = {
      'orgUserEmail': vendor.serviceAccountUsername,
      'orgUserPassword': vendor.serviceAccountPassword,
      'userEmail': vendor.individualUsername,
    };
    loginVendorPortal(payload).then(res => {
      setLoading(false);
      setToast({ 'type': 'SUCCESS', message: 'Login Successful' });
      return new Promise((resolve) => {
        const token = localStorage.getItem('AUTH_TOKEN');
        const redirectURL = `${FSConfig.smartboardUrl}/app/loads?token=${token}`;
        setToast({ 'type': 'INFO', message: 'Redirect to SmartBoard...' });
        setTimeout(() => {
          window.location.href = redirectURL; // or window.location.replace(url)
          resolve(); // Not necessary, but for chaining
        }, 2000);
      });
    })
      .catch(err => {
        setLoading(false);
        if (err.status === 400) {
          setToast({ 'type': 'ERROR', message: 'Invalid Login Credentials. Please check your login information.' });
        }

      })
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {INTEGRATIONS.map((integration) => {
          const isActivated = localStorage.getItem(integration.vendor);
          const status = isActivated ? 'ACTIVE' : 'INACTIVE';
          return (
            <Grid size={{ xs: 12, sm: 3 }} key={integration.vendor}>
              <Paper
                elevation={1}
                style={{
                  height: 'auto',
                  textAlign: 'center'
                }}
              >
                <Card>
                  <CardHeader
                    title={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Typography variant='h4' style={{ fontWeight: 600, letterSpacing: 1 }}>{integration.vendor}</Typography>
                        <Chip
                          size='small'
                          label={status}
                          variant='outlined'
                          style={{ color: getChipColor(status), borderColor: getChipColor(integration.status) }}
                        />
                      </Box>
                    }
                  />
                  <Divider />
                  <CardContent style={{
                    alignItems: 'center',
                    height: 250,
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <img alt='DATLogo' src={integration.logo} style={{ width: 150, height: 150 }} />
                    </Box>
                    <Typography variant='subtitle2'>{integration.description}</Typography>
                  </CardContent>
                  <Divider />
                  <CardActions disableSpacing style={{ display: 'inherit' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button size='small' color='primary' variant='outlined' disabled={isActivated} onClick={() => activateIntegration(integration.vendor)}>
                        Activate
                      </Button>
                      <Button size='small' color='primary' variant='contained' onClick={() => setVendorLoginDialogOpen(true)}>
                        Login
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      <DataDialog
        open={isVendorLoginDialogOpen}
        title='Login'
        isProcessing={isLoading}
        toast={toast}
        onCloseHandler={() => setVendorLoginDialogOpen(false)}
      >
        <Formik
          initialValues={initState.vendorLoginObj}
          validationSchema={
            Yup.object({
              serviceAccountUsername: Yup.string().email('Please enter service account email').max(100).required('Valid service account email is required.'),
              individualUsername: Yup.string().email('Please enter user email').max(100).required('Valid user email is required.'),
              serviceAccountPassword: Yup.string().required('Please enter service account password.')
            })
          }
          onSubmit={(values) => {
            handleLoginForm(values);
          }}
        >
          {({
            errors,
            touched,
            values,
            handleBlur,
            handleChange,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 4}}>
                  <InputField
                    label='Account Email'
                    name='serviceAccountUsername'
                    type='email'
                    value={values.serviceAccountUsername}
                    variant='outlined'
                    fullWidth
                    error={Boolean(touched.serviceAccountUsername && errors.serviceAccountUsername)}
                    helperText={touched.serviceAccountUsername && errors.serviceAccountUsername}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={{xs: 12, sm: 4}}>
                  <InputField
                    label='Account Password'
                    name='serviceAccountPassword'
                    type='password'
                    value={values.serviceAccountPassword}
                    variant='outlined'
                    fullWidth
                    error={Boolean(touched.serviceAccountPassword && errors.serviceAccountPassword)}
                    helperText={touched.serviceAccountPassword && errors.serviceAccountPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={{xs: 12, sm: 4}}>
                  <InputField
                    label='User Email'
                    name='individualUsername'
                    type='email'
                    value={values.individualUsername}
                    variant='outlined'
                    fullWidth
                    error={Boolean(touched.individualUsername && errors.individualUsername)}
                    helperText={touched.individualUsername && errors.individualUsername}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
                <Box
                  display='flex'
                  justifyContent='flex-end'
                  p={2}>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                  >
                    Login
                  </Button>
                </Box>
              </Grid>
            </Form>
          )
          }
        </Formik>
      </DataDialog>
    </Box>
  )
};

function mapStatesToProps(state) {
  const { tenant, user, users } = state.tenantReducer;

  return {
    tenant,
    user,
    users,
  };
}

// Promote TenantPage from a component to a container - it needs to know
// about this new dispatch method, selectTenant
export default connect(mapStatesToProps, null)(ThirdPartyIntegration);